import { useEffect, useRef, useState } from 'react';
import type { ObjectiveSummaryView } from '@contracts';
import { Check, ChevronDown } from '@ui/shared/icons';

/**
 * Objective picker field for habit create/edit (habit-form.md §1.5,
 * phase-3-plan.md HL-1/HL-5, AC-P1/AC-P5). Reuses DatePickerField's exact
 * closed-field-plus-popover shell/interaction pattern (same inputStyle
 * chrome, same open/close mechanics) rather than inventing a new control —
 * a flat single-select list body in place of DatePickerPopover's calendar
 * grid, since this is a short (<=5 active objectives, R3 cap), "None" +
 * list shape, not a date domain.
 */
export function ObjectivePickerField({
  value,
  activeObjectives,
  linkedTitleFallback,
  onChange,
}: {
  /** Selected objectiveId, or null for "None". */
  readonly value: string | null;
  /** Active objectives only (AC-P1: "active objectives... plus none") — the
   *  edit path's already-linked-but-now-archived case is handled by the
   *  caller resolving the closed-field label against a wider list; this
   *  popover's OWN offered options are always just the active set. */
  readonly activeObjectives: readonly ObjectiveSummaryView[];
  /**
   * habit-form.md §2 "Edit path, habit already linked": if `value` doesn't
   * resolve against `activeObjectives` (e.g. the linked objective has since
   * been archived), this supplies the closed field's display title instead
   * of falling back to "None" — an archived objectiveId is still a valid,
   * resolvable reference and must still be shown, even though the popover's
   * own list only offers active objectives to select from.
   */
  readonly linkedTitleFallback?: string | null;
  /** Selecting a row commits immediately (habit-form.md §1.5's "no separate
   *  apply step"); the caller decides whether that means local form state
   *  (create path) or an immediate habit.edited commit (edit path). */
  readonly onChange: (nextObjectiveId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = activeObjectives.find((o) => o.objectiveId === value) ?? null;
  const closedLabel = selected ? selected.title : value && linkedTitleFallback ? linkedTitleFallback : 'None';

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Linked objective: ${closedLabel}`}
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: closedLabel !== 'None' ? 'var(--text-primary)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          fontFamily: 'var(--font-family)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {closedLabel}
        </span>
        <ChevronDown size={20} color="var(--text-muted)" aria-hidden="true" />
      </button>

      <p
        style={{
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-secondary)',
          marginTop: 'var(--space-1)',
        }}
      >
        Linking shows this habit on the objective&apos;s detail page.
      </p>

      {open && (
        <ObjectivePickerPopover
          selected={value}
          activeObjectives={activeObjectives}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ObjectivePickerPopover({
  selected,
  activeObjectives,
  onSelect,
  onClose,
}: {
  readonly selected: string | null;
  readonly activeObjectives: readonly ObjectiveSummaryView[];
  readonly onSelect: (next: string | null) => void;
  readonly onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Same "register on the next tick" outside-click pattern as
  // TodayFooterMenu (today-view.md §1.5's bug-fix note, reused here per
  // habit-form.md §1.5's explicit instruction to match it) — avoids the
  // open-then-immediately-close race a same-render scrim would cause.
  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (popoverRef.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  useEffect(() => {
    itemRefs.current[0]?.focus();
  }, []);

  const rowCount = activeObjectives.length + 1; // +1 for "None"

  function handleKeyDown(e: React.KeyboardEvent) {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        itemRefs.current[(current + 1 + rowCount) % rowCount]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        itemRefs.current[(current - 1 + rowCount) % rowCount]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  }

  return (
    <div
      ref={popoverRef}
      role="listbox"
      aria-label="Linked objective"
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-2))',
        left: 0,
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <button
        ref={(el) => {
          itemRefs.current[0] = el;
        }}
        type="button"
        role="option"
        aria-selected={selected === null}
        onClick={() => onSelect(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'var(--touch-target-min)',
          width: '100%',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          textAlign: 'left',
        }}
      >
        None
        {selected === null && <Check size={16} color="var(--hue-primary)" aria-hidden="true" />}
      </button>

      {activeObjectives.map((objective, i) => {
        const isSelected = objective.objectiveId === selected;
        return (
          <button
            key={objective.objectiveId}
            ref={(el) => {
              itemRefs.current[i + 1] = el;
            }}
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(objective.objectiveId)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              width: '100%',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontWeight: 500,
              fontSize: 'var(--type-body-size)',
              textAlign: 'left',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {objective.title}
            </span>
            {isSelected && <Check size={16} color="var(--hue-primary)" aria-hidden="true" />}
          </button>
        );
      })}

      {activeObjectives.length === 0 && (
        <p
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-secondary)',
            padding: '0 var(--space-3)',
            marginTop: 'var(--space-2)',
            marginBottom: 0,
          }}
        >
          No active objectives yet — create one to link this habit later.
        </p>
      )}
    </div>
  );
}
